<template>
  <div class="dashboard">
    <div class="header">
      <div class="header__top">
        <div>
          <h2 class="mb-25">
            {{ $t('referralDashboard.title') }}
          </h2>
          <p class="text-muted mb-0">
            {{ isGlobalAdminView ? $t('referralDashboard.globalSubtitle') : $t('referralDashboard.userSubtitle') }}
          </p>
        </div>

        <div class="header__controls">
          <button
            v-if="isGlobalAdminView"
            class="btn-my-commissions"
            type="button"
            :disabled="loading"
            @click="goToMyCommissions"
          >
            {{ $t('referralDashboard.myCommissionsAction') }}
          </button>

          <div class="date-selector">
            <label for="month">{{ $t('referralDashboard.month') }}:</label>
            <select
              id="month"
              v-model="selectedMonth"
              :disabled="loading"
              @change="onPeriodChange"
            >
              <option
                v-for="month in monthOptions"
                :key="month.value"
                :value="month.value"
              >
                {{ month.label }}
              </option>
            </select>

            <label for="year">{{ $t('referralDashboard.year') }}:</label>
            <select
              id="year"
              v-model="selectedYear"
              :disabled="loading"
              @change="onPeriodChange"
            >
              <option
                v-for="year in years"
                :key="year"
                :value="year"
              >
                {{ year }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="summary">
        <template v-if="isGlobalAdminView">
          <div class="stat-grid">
            <div class="stat stat--wide">
              <span class="stat__label">{{ $t('referralDashboard.totalCommission') }}</span>
              <strong class="stat__value stat__value--money">{{ formatCurrency(globalData.total_commission) }}</strong>
            </div>
            <div class="stat stat--wide">
              <span class="stat__label">{{ $t('referralDashboard.lifetimeCommission') }}</span>
              <strong class="stat__value stat__value--money">{{ formatCurrency(globalData.lifetime_commission) }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.totalUsers') }}</span>
              <strong class="stat__value">{{ formatNumber(globalData.total_users) }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.totalSales') }}</span>
              <strong class="stat__value">{{ formatCurrency(globalData.total_sales) }}</strong>
            </div>
            <div class="stat stat--wide">
              <span class="stat__label">{{ $t('referralDashboard.myAffiliateCode') }}</span>
              <strong class="stat__value stat__value--code">{{ adminSelfData.affiliate_code || '-' }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.myCommission') }}</span>
              <strong class="stat__value">{{ formatCurrency(adminSelfData.total_commission) }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.myLifetimeCommission') }}</span>
              <strong class="stat__value">{{ formatCurrency(adminSelfData.lifetime_commission) }}</strong>
              <span
                v-if="lifetimeSinceLabel(adminSelfData)"
                class="stat__hint"
              >
                {{ lifetimeSinceLabel(adminSelfData) }}
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="stat-grid">
            <div class="stat stat--wide">
              <span class="stat__label">{{ $t('referralDashboard.lifetimeCommission') }}</span>
              <strong class="stat__value stat__value--money">{{ formatCurrency(dashboardData.lifetime_commission) }}</strong>
              <span
                v-if="lifetimeSinceLabel(dashboardData)"
                class="stat__hint"
              >
                {{ lifetimeSinceLabel(dashboardData) }}
              </span>
            </div>
            <div class="stat stat--wide">
              <span class="stat__label">{{ $t('referralDashboard.affiliateCode') }}</span>
              <strong class="stat__value stat__value--code">{{ dashboardData.affiliate_code || '-' }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.totalRegisteredReferrals') }}</span>
              <strong class="stat__value">{{ formatNumber(dashboardData.total_registered_referred_customers) }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.referredCustomersWithPurchases') }}</span>
              <strong class="stat__value">{{ formatNumber(dashboardData.total_referred_customers_with_purchases) }}</strong>
            </div>
            <div class="stat">
              <span class="stat__label">{{ $t('referralDashboard.conversionRate') }}</span>
              <strong class="stat__value">{{ formatPercentage(conversionRate) }}</strong>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="!isGlobalAdminView && formulaData"
      class="formula-panel"
    >
      <div class="formula-header">
        <h3 class="mb-25">
          {{ $t('referralDashboard.formula.title') }}
        </h3>
        <p class="text-muted mb-0">
          {{ $t('referralDashboard.formula.subtitle') }}
        </p>
      </div>

      <div class="formula-metrics">
        <div class="metric-card">
          <span class="metric-label">{{ $t('referralDashboard.formula.referredCustomersWithSales') }}</span>
          <strong class="metric-value">{{ formatNumber(formulaData.referred_customers_with_sales) }}</strong>
        </div>
        <div class="metric-card">
          <span class="metric-label">{{ $t('referralDashboard.formula.totalSales') }}</span>
          <strong class="metric-value">{{ formatCurrency(formulaData.total_sales) }}</strong>
        </div>
        <div class="metric-card">
          <span class="metric-label">{{ $t('referralDashboard.formula.commissionableBase') }}</span>
          <strong class="metric-value">{{ formatCurrency(formulaData.commissionable_base) }}</strong>
        </div>
        <div class="metric-card">
          <span class="metric-label">{{ $t('referralDashboard.formula.totalCommission') }}</span>
          <strong class="metric-value">{{ formatCurrency(formulaData.total_commission) }}</strong>
        </div>
      </div>

      <div class="formula-equation">
        {{ formatPercentage(formulaData.base_percentage) }}
        +
        {{ formatPercentage(formulaData.user_tier_bonus_percentage) }}
        +
        {{ formatPercentage(formulaData.sales_tier_bonus_percentage) }}
        =
        <strong>{{ formatPercentage(formulaData.final_percentage) }}</strong>
      </div>

      <div class="formula-equation secondary">
        {{ formatCurrency(formulaData.commissionable_base) }}
        x
        {{ formatPercentage(formulaData.final_percentage) }}
        =
        <strong>{{ formatCurrency(formulaData.total_commission) }}</strong>
      </div>

      <div class="formula-next-steps">
        <p v-if="formulaData.next_user_tier">
          {{ $t('referralDashboard.formula.nextUserTier', { missing: formulaData.next_user_tier.missing, target: formulaData.next_user_tier.target_min, bonus: formatPercentage(formulaData.next_user_tier.bonus) }) }}
        </p>
        <p v-else>
          {{ $t('referralDashboard.formula.userTierMaxReached') }}
        </p>
        <p v-if="formulaData.next_sales_tier">
          {{ $t('referralDashboard.formula.nextSalesTier', { missing: formatCurrency(formulaData.next_sales_tier.missing), target: formatCurrency(formulaData.next_sales_tier.target_min), bonus: formatPercentage(formulaData.next_sales_tier.bonus) }) }}
        </p>
        <p v-else>
          {{ $t('referralDashboard.formula.salesTierMaxReached') }}
        </p>
      </div>

      <div class="tiers-grid">
        <div class="tiers-block">
          <h4>{{ $t('referralDashboard.formula.userTierTableTitle') }}</h4>
          <table class="tiers-table">
            <thead>
              <tr>
                <th>{{ $t('referralDashboard.formula.range') }}</th>
                <th>{{ $t('referralDashboard.formula.bonus') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tier in formulaData.user_tiers || []"
                :key="`u-${tier.min}-${tier.max || 'max'}`"
                :class="tierRowClass('user', tier)"
              >
                <td>
                  {{ formatTierRange(tier) }}
                  <span
                    v-if="isTierCurrent('user', tier)"
                    class="tier-badge current"
                  >
                    {{ $t('referralDashboard.formula.currentTier') }}
                  </span>
                  <span
                    v-else-if="isTierNext('user', tier)"
                    class="tier-badge next"
                  >
                    {{ $t('referralDashboard.formula.nextTier') }}
                  </span>
                </td>
                <td>{{ formatPercentage(tier.bonus) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="tiers-block">
          <h4>{{ $t('referralDashboard.formula.salesTierTableTitle') }}</h4>
          <table class="tiers-table">
            <thead>
              <tr>
                <th>{{ $t('referralDashboard.formula.range') }}</th>
                <th>{{ $t('referralDashboard.formula.bonus') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tier in formulaData.sales_tiers || []"
                :key="`s-${tier.min}-${tier.max || 'max'}`"
                :class="tierRowClass('sales', tier)"
              >
                <td>
                  {{ formatMoneyTierRange(tier) }}
                  <span
                    v-if="isTierCurrent('sales', tier)"
                    class="tier-badge current"
                  >
                    {{ $t('referralDashboard.formula.currentTier') }}
                  </span>
                  <span
                    v-else-if="isTierNext('sales', tier)"
                    class="tier-badge next"
                  >
                    {{ $t('referralDashboard.formula.nextTier') }}
                  </span>
                </td>
                <td>{{ formatPercentage(tier.bonus) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="isGlobalAdminView"
      class="filters"
    >
      <input
        v-model.trim="filters.q"
        class="filter-input"
        :placeholder="$t('referralDashboard.searchUserOrCustomerPlaceholder')"
        :disabled="loading"
        @keyup.enter="applyFilters"
      >
      <input
        v-model.trim="filters.affiliateCode"
        class="filter-input"
        :placeholder="$t('referralDashboard.searchAffiliateCodePlaceholder')"
        :disabled="loading"
        @keyup.enter="applyFilters"
      >
      <button
        class="filter-button search"
        type="button"
        :disabled="loading"
        @click="applyFilters"
      >
        {{ $t('referralDashboard.search') }}
      </button>
      <button
        class="filter-button clear"
        type="button"
        :disabled="loading || !hasActiveFilters"
        @click="clearFilters"
      >
        {{ $t('referralDashboard.clear') }}
      </button>
    </div>

    <!-- Móvil: tarjetas en vez de tabla -->
    <div
      v-if="isSmallScreen"
      class="ref-cards"
    >
      <p
        v-if="!sortedRows.length"
        class="ref-empty"
      >
        {{ $t('referralDashboard.empty') }}
      </p>

      <template v-if="isGlobalAdminView">
        <div
          v-for="row in sortedRows"
          :key="rowKey(row)"
          :class="['ref-card', { 'is-clickable': canOpenUserDetail(row) }]"
          @click="handleRowClick(row)"
        >
          <div class="ref-card__top">
            <span class="ref-card__title">{{ row.user_name || '-' }}</span>
            <span class="ref-card__amount">{{ formatCurrency(row.total_commission) }}</span>
          </div>
          <div class="ref-card__meta">
            <span>{{ $t('referralDashboard.columns.affiliateCode') }}: <strong>{{ row.affiliate_code || '-' }}</strong></span>
          </div>
          <div class="ref-card__chips">
            <span class="ref-chip">
              {{ $t('referralDashboard.columns.referredCount') }}: {{ row.referred_customers_count || 0 }}
            </span>
            <span class="ref-chip">
              {{ $t('referralDashboard.columns.totalSales') }}: {{ formatCurrency(row.total_sales) }}
            </span>
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="row in sortedRows"
          :key="rowKey(row)"
          class="ref-card"
        >
          <div class="ref-card__top">
            <span class="ref-card__title">{{ row.customer || '-' }}</span>
            <span class="ref-card__amount">{{ formatCurrency(row.total_commission) }}</span>
          </div>
          <div class="ref-card__meta">
            <span :class="['status-badge', row.status || 'registered']">
              {{ referralStatusLabel(row.status) }}
            </span>
            <span class="ref-card__date">{{ row.registration_date || '-' }}</span>
          </div>
          <div class="ref-card__chips">
            <span class="ref-chip">
              {{ $t('referralDashboard.columns.projects') }}: {{ row.projects_count || 0 }}
            </span>
            <span class="ref-chip">
              {{ $t('referralDashboard.columns.quotes') }}: {{ row.quotes_count || 0 }}
            </span>
            <span class="ref-chip">
              {{ $t('referralDashboard.columns.totalPurchases') }}: {{ row.total_purchases || 0 }}
            </span>
          </div>
          <div class="ref-card__foot">
            {{ $t('referralDashboard.columns.lastPurchaseDate') }}:
            {{ row.last_purchase_date || $t('referralDashboard.noPurchases') }}
          </div>
        </div>
      </template>
    </div>

    <!-- Escritorio: tabla -->
    <table
      v-else
      class="details-table"
    >
      <thead>
        <tr>
          <th
            v-for="column in tableColumns"
            :key="column.key"
            @click="sortTable(column.key)"
          >
            {{ $t(column.label) }}
            <span v-if="sortField === column.key">
              <i :class="sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'"></i>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in sortedRows"
          :key="rowKey(row)"
          :class="{ 'clickable-row': isGlobalAdminView && canOpenUserDetail(row) }"
          @click="handleRowClick(row)"
        >
          <template v-if="isGlobalAdminView">
            <td class="global-user-cell">
              <span class="user-link-like">{{ row.user_name || '-' }}</span>
              <span
                v-if="canOpenUserDetail(row)"
                class="row-hint"
              >
                {{ $t('referralDashboard.clickForDetail') }}
              </span>
            </td>
            <td>{{ row.affiliate_code || '-' }}</td>
            <td>{{ row.referred_customers_count || 0 }}</td>
            <td>{{ formatCurrency(row.total_sales) }}</td>
            <td>{{ formatCurrency(row.total_commission) }}</td>
          </template>
          <template v-else>
            <td>{{ row.registration_date || '-' }}</td>
            <td>{{ row.customer || '-' }}</td>
            <td>
              <span :class="['status-badge', row.status || 'registered']">
                {{ referralStatusLabel(row.status) }}
              </span>
            </td>
            <td>{{ row.projects_count || 0 }}</td>
            <td>{{ row.quotes_count || 0 }}</td>
            <td>{{ row.total_purchases || 0 }}</td>
            <td>{{ formatCurrency(row.total_commission) }}</td>
            <td>{{ row.last_purchase_date || $t('referralDashboard.noPurchases') }}</td>
            <td>{{ row.last_activity_date || '-' }}</td>
          </template>
        </tr>
        <tr v-if="!sortedRows.length">
          <td
            class="empty-cell"
            :colspan="tableColumns.length"
          >
            {{ $t('referralDashboard.empty') }}
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="isGlobalAdminView"
      class="pagination-wrap"
    >
      <p class="pagination-summary">
        {{ paginationSummary }}
      </p>
      <b-pagination
        v-model="globalPagination.page"
        :total-rows="globalPagination.total_objects"
        :per-page="globalPagination.per_page"
        :disabled="loading"
        first-number
        last-number
        @input="onGlobalPageChange"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ReferralsDashboard',
  data() {
    const currentYear = new Date().getFullYear()
    return {
      loading: false,
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: currentYear,
      years: [currentYear - 1, currentYear, currentYear + 1],
      dashboardData: {
        affiliate_code: '',
        total_commission: 0,
        lifetime_commission: 0,
        lifetime_first_period: null,
        total_registered_referred_customers: 0,
        total_referred_customers_with_purchases: 0,
        customers: [],
      },
      globalData: {
        users: [],
        total_users: 0,
        total_sales: 0,
        total_commission: 0,
        lifetime_commission: 0,
        meta: {
          pagination: {
            page: 1,
            per_page: 10,
            total_objects: 0,
            total_pages: 1,
          },
        },
      },
      adminSelfData: {
        affiliate_code: '',
        total_commission: 0,
        lifetime_commission: 0,
        lifetime_first_period: null,
      },
      filters: {
        q: '',
        affiliateCode: '',
      },
      globalPagination: {
        page: 1,
        per_page: 10,
        total_objects: 0,
        total_pages: 1,
      },
      sortField: 'registration_date',
      sortOrder: 'asc',
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('customers', ['selectedCustomerResourceId']),
    ...mapGetters('app', ['isSmallScreen']),
    isAdmin() {
      return this.currentUser?.role_name === 'admin'
    },
    routeCustomerId() {
      return this.$route?.params?.customerId || null
    },
    routeUserId() {
      return this.$route?.query?.userId || null
    },
    resolvedCustomerId() {
      if (this.isAdmin) return this.routeCustomerId || null
      return this.currentUser?.scoped_roles?.[0]?.role_resource_id || this.routeCustomerId || this.selectedCustomerResourceId || null
    },
    resolvedUserId() {
      if (!this.isAdmin) return null
      return this.routeUserId || null
    },
    isGlobalAdminView() {
      return this.isAdmin && this.$route?.name === 'referralsGlobal'
    },
    monthOptions() {
      const locale = this.$i18n?.locale === 'es' ? 'es-MX' : this.$i18n?.locale || 'es-MX'
      return Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: new Date(2000, index, 1).toLocaleString(locale, { month: 'long' }),
      }))
    },
    tableColumns() {
      if (this.isGlobalAdminView) {
        return [
          { key: 'user_name', label: 'referralDashboard.columns.user' },
          { key: 'affiliate_code', label: 'referralDashboard.columns.affiliateCode' },
          { key: 'referred_customers_count', label: 'referralDashboard.columns.referredCount' },
          { key: 'total_sales', label: 'referralDashboard.columns.totalSales' },
          { key: 'total_commission', label: 'referralDashboard.columns.totalCommission' },
        ]
      }

      return [
        { key: 'registration_date', label: 'referralDashboard.columns.registrationDate' },
        { key: 'customer', label: 'referralDashboard.columns.customer' },
        { key: 'status', label: 'referralDashboard.columns.status' },
        { key: 'projects_count', label: 'referralDashboard.columns.projects' },
        { key: 'quotes_count', label: 'referralDashboard.columns.quotes' },
        { key: 'total_purchases', label: 'referralDashboard.columns.totalPurchases' },
        { key: 'total_commission', label: 'referralDashboard.columns.periodCommission' },
        { key: 'last_purchase_date', label: 'referralDashboard.columns.lastPurchaseDate' },
        { key: 'last_activity_date', label: 'referralDashboard.columns.lastActivityDate' },
      ]
    },
    tableRows() {
      return this.isGlobalAdminView
        ? (this.globalData.users || [])
        : (this.dashboardData.customers || [])
    },
    formulaData() {
      return this.dashboardData?.formula || null
    },
    conversionRate() {
      const totalRegistered = Number(this.dashboardData?.total_registered_referred_customers || 0)
      const totalWithPurchases = Number(this.dashboardData?.total_referred_customers_with_purchases || 0)
      if (!totalRegistered) return 0
      return (totalWithPurchases / totalRegistered) * 100
    },
    hasActiveFilters() {
      return Boolean(this.filters.q || this.filters.affiliateCode)
    },
    paginationSummary() {
      if (!this.globalPagination.total_objects) return this.$t('referralDashboard.empty')
      const page = Number(this.globalPagination.page || 1)
      const perPage = Number(this.globalPagination.per_page || 10)
      const total = Number(this.globalPagination.total_objects || 0)
      const start = ((page - 1) * perPage) + 1
      const end = Math.min(page * perPage, total)
      return `${this.$t('referralDashboard.showing')} ${start}-${end} ${this.$t('referralDashboard.of')} ${total}`
    },
    sortedRows() {
      const rows = [...this.tableRows]
      return rows.sort((a, b) => {
        const valA = a[this.sortField]
        const valB = b[this.sortField]

        const normalizedA = this.normalizeSortValue(valA)
        const normalizedB = this.normalizeSortValue(valB)

        if (normalizedA > normalizedB) return this.sortOrder === 'asc' ? 1 : -1
        if (normalizedA < normalizedB) return this.sortOrder === 'asc' ? -1 : 1
        return 0
      })
    },
  },
  watch: {
    '$route.name': {
      handler() {
        this.ensureValidSortField()
        this.resetGlobalPagination()
        this.updateDashboard()
      },
    },
    '$route.params.customerId': {
      handler() {
        this.ensureValidSortField()
        this.updateDashboard()
      },
    },
    '$route.query.userId': {
      handler() {
        this.ensureValidSortField()
        this.updateDashboard()
      },
    },
  },
  mounted() {
    this.ensureValidSortField()
    this.updateDashboard()
  },
  methods: {
    ...mapActions('users', ['fetchDashboard', 'fetchDashboardGlobal']),
    resetGlobalPagination() {
      this.globalPagination = {
        page: 1,
        per_page: this.globalPagination?.per_page || 10,
        total_objects: 0,
        total_pages: 1,
      }
    },
    onGlobalPageChange(page) {
      this.globalPagination.page = Number(page || 1)
      this.updateDashboard()
    },
    onPeriodChange() {
      if (this.isGlobalAdminView) {
        this.globalPagination.page = 1
      }
      this.updateDashboard()
    },
    applyFilters() {
      this.globalPagination.page = 1
      this.updateDashboard()
    },
    clearFilters() {
      this.filters.q = ''
      this.filters.affiliateCode = ''
      this.globalPagination.page = 1
      this.updateDashboard()
    },
    canOpenUserDetail(row) {
      return Boolean(row?.customer_id || row?.user_id)
    },
    openUserDetail(row) {
      if (!this.canOpenUserDetail(row)) return

      if (row.customer_id) {
        this.$router.push({
          name: 'referrals',
          params: { customerId: row.customer_id },
        })
        return
      }

      this.$router.push({
        name: 'referrals',
        query: { userId: row.user_id },
      })
    },
    handleRowClick(row) {
      if (!this.isGlobalAdminView) return
      this.openUserDetail(row)
    },
    goToMyCommissions() {
      this.$router.push({ name: 'referrals' })
    },
    rowKey(row) {
      if (this.isGlobalAdminView) return row.user_id || row.affiliate_code || row.user_name
      return `${row.registration_date || ''}-${row.customer || ''}-${row.last_purchase_date || ''}`
    },
    normalizeSortValue(value) {
      if (value == null) return ''
      const asNumber = Number(value)
      if (!Number.isNaN(asNumber) && value !== '') return asNumber
      return String(value).toLowerCase()
    },
    ensureValidSortField() {
      const availableFields = this.tableColumns.map(column => column.key)
      if (!availableFields.includes(this.sortField)) {
        const [firstField] = availableFields
        this.sortField = firstField
        this.sortOrder = 'asc'
      }
    },
    async updateDashboard() {
      this.loading = true
      try {
        if (this.isGlobalAdminView) {
          const [globalData, selfData] = await Promise.all([
            this.fetchDashboardGlobal({
              month: this.selectedMonth,
              year: this.selectedYear,
              q: this.filters.q || null,
              affiliateCode: this.filters.affiliateCode || null,
              pagination: {
                page: this.globalPagination.page,
                per_page: this.globalPagination.per_page,
              },
            }),
            this.fetchDashboard({
              month: this.selectedMonth,
              year: this.selectedYear,
              userId: this.currentUser?.id || null,
            }),
          ])
          this.globalData = globalData || { users: [], lifetime_commission: 0 }
          this.globalPagination = {
            ...(this.globalPagination || {}),
            ...(globalData?.meta?.pagination || {}),
          }
          this.adminSelfData = selfData || {
            affiliate_code: '', total_commission: 0, lifetime_commission: 0, lifetime_first_period: null,
          }
          return
        }

        const data = await this.fetchDashboard({
          month: this.selectedMonth,
          year: this.selectedYear,
          customerId: this.isAdmin ? this.resolvedCustomerId : null,
          userId: this.isAdmin ? this.resolvedUserId : null,
        })
        this.dashboardData = data || { customers: [], lifetime_commission: 0, lifetime_first_period: null }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching dashboard data:', error)
      } finally {
        this.loading = false
      }
    },
    formatCurrency(value) {
      const amount = Number(value || 0)
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(amount)
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-MX').format(Number(value || 0))
    },
    lifetimeSinceLabel(data) {
      const period = data?.lifetime_first_period
      if (!period?.month || !period?.year) return ''
      const monthLabel = this.monthOptions.find(option => option.value === Number(period.month))?.label
      if (!monthLabel) return ''
      return this.$t('referralDashboard.lifetimeSince', { month: monthLabel, year: period.year })
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(2)}%`
    },
    referralStatusLabel(status) {
      switch (status) {
        case 'purchased':
          return this.$t('referralDashboard.status.purchased')
        case 'quoted':
          return this.$t('referralDashboard.status.quoted')
        case 'with_project':
          return this.$t('referralDashboard.status.withProject')
        default:
          return this.$t('referralDashboard.status.registered')
      }
    },
    formatTierRange(tier) {
      const maxLabel = this.$t('referralDashboard.formula.orMore')
      if (tier.max == null) return `${this.formatNumber(tier.min)} ${maxLabel}`
      return `${this.formatNumber(tier.min)} - ${this.formatNumber(tier.max)}`
    },
    formatMoneyTierRange(tier) {
      const maxLabel = this.$t('referralDashboard.formula.orMore')
      if (tier.max == null) return `${this.formatCurrency(tier.min)} ${maxLabel}`
      return `${this.formatCurrency(tier.min)} - ${this.formatCurrency(tier.max)}`
    },
    isTierCurrent(type, tier) {
      if (!this.formulaData) return false
      const value = type === 'user'
        ? Number(this.formulaData.referred_customers_with_sales || 0)
        : Number(this.formulaData.total_sales || 0)
      return value >= Number(tier.min) && (tier.max == null || value <= Number(tier.max))
    },
    isTierNext(type, tier) {
      if (!this.formulaData) return false
      const nextTier = type === 'user' ? this.formulaData.next_user_tier : this.formulaData.next_sales_tier
      if (!nextTier) return false
      return Number(nextTier.target_min) === Number(tier.min)
    },
    tierRowClass(type, tier) {
      return {
        'is-current': this.isTierCurrent(type, tier),
        'is-next': this.isTierNext(type, tier),
      }
    },
    sortTable(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortOrder = 'asc'
      }
    },
  },
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
  margin: auto;
  max-width: 95%;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  border-radius: 8px;
}

/* Tiles de resumen */
.stat-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  text-align: left;
}

.stat {
  background: #f7f8fc;
  border: 1px solid #eaecf4;
  border-radius: 8px;
  padding: 8px 10px;
}

.stat__label {
  color: #666a80;
  display: block;
  font-size: 11px;
  margin-bottom: 2px;
}

.stat__value {
  color: #2f1558;
  font-size: 16px;
  overflow-wrap: anywhere;
}

.stat__value--money {
  font-size: 20px;
}

.stat__value--code {
  letter-spacing: 0.04em;
}

.stat__hint {
  color: #6b6f85;
  display: block;
  font-size: 11px;
  margin-top: 2px;
}

/* Tarjetas móviles del detalle */
.ref-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.ref-card {
  background: #fff;
  border: 1px solid #e3e5ee;
  border-radius: 10px;
  padding: 12px;
}

.ref-card.is-clickable {
  cursor: pointer;
}

.ref-card__top {
  align-items: baseline;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.ref-card__title {
  color: #2f1558;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.ref-card__amount {
  color: #1f8f53;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ref-card__meta {
  align-items: center;
  color: #6b6f85;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 8px;
  margin-top: 6px;
}

.ref-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.ref-chip {
  background: #f4f5fa;
  border-radius: 999px;
  color: #464a63;
  font-size: 11px;
  padding: 3px 9px;
}

.ref-card__foot {
  border-top: 1px dashed #eaecf4;
  color: #6b6f85;
  font-size: 12px;
  margin-top: 8px;
  padding-top: 6px;
}

.ref-empty {
  color: #6b6f85;
  font-style: italic;
  padding: 16px 0;
  text-align: center;
}

.header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
  padding: 14px 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.header__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.header__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.summary p {
  margin: 5px 0;
  font-size: 16px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 102px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.status-badge.registered {
  background: #eef1f7;
  color: #5b6275;
}

.status-badge.with_project {
  background: #e8f3ff;
  color: #1768aa;
}

.status-badge.quoted {
  background: #fff3e6;
  color: #b26a14;
}

.status-badge.purchased {
  background: #e8f8ef;
  color: #1f8f53;
}

.btn-my-commissions {
  border: 1px solid #0e8f38;
  background: #20b15a;
  color: #fff;
  border-radius: 6px;
  padding: 7px 12px;
  font-weight: 700;
  font-size: 13px;
}

.date-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.formula-panel {
  margin: 10px 0 18px;
  border: 1px solid #e3e5ee;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
}

.formula-header {
  margin-bottom: 12px;
}

.formula-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.metric-card {
  background: #f7f8fc;
  border: 1px solid #eaecf4;
  border-radius: 8px;
  padding: 10px;
}

.metric-label {
  display: block;
  color: #666a80;
  font-size: 12px;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 16px;
  color: #2f1558;
}

.formula-equation {
  background: #f7f8fc;
  border: 1px solid #eaecf4;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  color: #464a63;
  margin-bottom: 8px;
}

.formula-equation.secondary {
  margin-bottom: 12px;
}

.formula-next-steps {
  border: 1px dashed #ced3e3;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 14px;
}

.formula-next-steps p {
  margin: 0 0 6px;
  font-size: 13px;
  color: #575c76;
}

.formula-next-steps p:last-child {
  margin-bottom: 0;
}

.tiers-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.tiers-block h4 {
  font-size: 14px;
  margin: 0 0 8px;
  color: #40455e;
}

.tiers-table {
  width: 100%;
  border-collapse: collapse;
}

.tiers-table th,
.tiers-table td {
  border: 1px solid #eaecf4;
  padding: 6px 8px;
  font-size: 12px;
  text-align: left;
}

.tiers-table th {
  background: #f4f5fa;
}

.tiers-table tr.is-current td {
  background: #e8fff1;
  border-color: #87d7a6;
}

.tiers-table tr.is-next td {
  background: #f3f7ff;
  border-color: #b7c8ef;
}

.tier-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  vertical-align: middle;
}

.tier-badge.current {
  color: #0a6f34;
  background: #d7f6e3;
  border: 1px solid #8ed0a8;
}

.tier-badge.next {
  color: #2f4c8a;
  background: #e8efff;
  border: 1px solid #b3c4ec;
}

.filters {
  display: grid;
  grid-template-columns: 1.2fr 1fr auto auto;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
}

.filter-input {
  border: 1px solid #d2d4df;
  border-radius: 6px;
  padding: 10px;
  background: #fff;
}

.filter-button {
  border-radius: 6px;
  padding: 9px 14px;
  border: 1px solid transparent;
  font-weight: 700;
  font-size: 13px;
}

.filter-button.search {
  background: #2f1558;
  color: #fff;
}

.filter-button.clear {
  background: #fff;
  border-color: #2f1558;
  color: #2f1558;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.details-table th,
.details-table td {
  border: 1px solid #ccc;
  padding: 12px;
  text-align: center;
  font-size: 14px;
}

.details-table th {
  background-color: #f0f0f0;
  font-weight: bold;
  cursor: pointer;
}

.details-table th:hover {
  background-color: #e0e0e0;
}

.details-table tbody tr:hover {
  background-color: #f5f5f5;
}

.details-table tbody tr.clickable-row {
  cursor: pointer;
}

.details-table tbody tr.clickable-row:hover td {
  background: #f6f9ff;
}

.global-user-cell {
  text-align: left;
}

.user-link-like {
  color: #2f1558;
  font-weight: 700;
}

.row-hint {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #6f7390;
}

.empty-cell {
  text-align: center;
  font-style: italic;
}

.arrow-up::before {
  content: '▲';
  margin-left: 5px;
  font-size: 12px;
  color: #555;
}

.arrow-down::before {
  content: '▼';
  margin-left: 5px;
  font-size: 12px;
  color: #555;
}

.pagination-wrap {
  margin-top: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pagination-summary {
  margin: 0;
  color: #6b6f85;
  font-size: 13px;
}

@media (max-width: 900px) {
  .header {
    gap: 12px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat--wide {
    grid-column: span 2;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .formula-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tiers-grid {
    grid-template-columns: 1fr;
  }
}

/* Celular: la app ya tiene su propio marco, el panel deja de ser una "tarjeta"
   y se reserva espacio para el menú inferior fijo (65px). */
@media (max-width: 576px) {
  .dashboard {
    background-color: #fff;
    border: 0;
    border-radius: 0;
    margin: 0;
    max-width: 100%;
    padding: 12px 12px 88px;
  }

  .header {
    box-shadow: none;
    padding: 0;
  }

  .header h2 {
    font-size: 1.15rem;
  }

  .header__controls {
    width: 100%;
  }

  .btn-my-commissions {
    width: 100%;
  }

  .date-selector {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px 10px;
    justify-content: stretch;
    margin: 0;
    width: 100%;
  }

  .date-selector select {
    border: 1px solid #d2d4df;
    border-radius: 6px;
    min-height: 40px;
    padding: 6px 8px;
    width: 100%;
  }

  .formula-panel {
    padding: 12px;
  }

  .formula-metrics {
    gap: 8px;
  }

  /* Las ecuaciones y rangos no deben desbordar la pantalla */
  .formula-equation {
    overflow-wrap: anywhere;
    line-height: 1.5;
  }

  .tiers-table th,
  .tiers-table td {
    padding: 6px;
  }

  .tier-badge {
    display: inline-block;
    margin: 3px 0 0 4px;
  }

  .pagination-wrap {
    justify-content: center;
  }
}
</style>
